import React, { useCallback, useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions
} from '@headlessui/react';
import { ChevronDown, UserCog } from 'lucide-react';
import { clsx } from 'clsx';
import { useRole } from 'features/auth/contexts/RoleContext';
import { isSupportedSwitchRole, portailLabel } from 'core/roles';

interface DashboardMenuRoleSwitcherProps {
  isCollapsed: boolean;
}

interface RoleItem {
  key: string;
  label: string;
  /** Nom envoyé au back (rôle ou portail selon le mode). */
  value: string;
  /** Expérience front non gérée pour ce rôle : option grisée, non cliquable. */
  disabled: boolean;
}

export const DashboardMenuRoleSwitcher: React.FC<
  DashboardMenuRoleSwitcherProps
> = ({ isCollapsed }) => {
  const { t } = useTranslation(NsKey.Common);
  const {
    session,
    currentRole,
    roles,
    portailActuel,
    portails,
    isLoading,
    switchRole,
    switchPortail
  } = useRole();
  const id = useId();
  const estModePortail = session.mode === 'portail';

  const handleRoleSwitch = useCallback(
    async (item: RoleItem) => {
      // Rôle non géré (grisé) : aucun switch tant que son expérience n'est pas conçue.
      if (!item || item.disabled) {
        return;
      }
      if (estModePortail) {
        if (item.value === portailActuel?.nom) return;
        await switchPortail(item.value);
        return;
      }
      if (item.key === currentRole?.roleId) return;
      // Contrat back : switch par NOM de rôle (body { roleName })
      await switchRole(item.value);
    },
    [estModePortail, portailActuel, currentRole, switchRole, switchPortail]
  );

  // Rôle actuel mis en avant : bloc contrasté toujours visible (pas seulement au hover),
  // liseré + surtitre « Rôle actuel » pour qu'on identifie le rôle actif d'un coup d'œil.
  const buttonClasses = useCallback(
    (open: boolean) =>
      clsx(
        'w-full flex items-center justify-between p-3 rounded-xl transition-all duration-200',
        'text-sm ring-1 ring-inset',
        open
          ? 'bg-white/25 text-white ring-white/40'
          : 'bg-white/10 text-white ring-white/20 hover:bg-white/20 hover:ring-white/30'
      ),
    []
  );

  const menuClasses = useMemo(
    () =>
      clsx(
        'w-[var(--button-width)] bg-white z-50 overflow-y-auto rounded-lg shadow-lg',
        '[--anchor-max-height:40vh]'
      ),
    []
  );

  const getItemClasses = useCallback(
    (focus: boolean, selected: boolean, disabled: boolean) =>
      clsx(
        'px-3 py-2 flex justify-start text-sm transition-colors',
        'border-b border-gray-100 last:border-b-0',
        disabled
          ? 'cursor-not-allowed text-gray-300 bg-gray-50/60 select-none'
          : selected
            ? 'bg-primary text-white font-medium cursor-pointer'
            : focus
              ? 'bg-primary/10 text-primary cursor-pointer'
              : 'text-gray-700 hover:bg-gray-50 cursor-pointer'
      ),
    []
  );

  if (isLoading || isCollapsed) {
    return null;
  }

  // Mode portail : le sélecteur liste les portails accessibles ; mode legacy :
  // les rôles assignés (seuls les rôles à expérience gérée sont cliquables).
  let items: RoleItem[];
  let selectedItem: RoleItem | null;

  if (estModePortail) {
    if (!portailActuel || portails.length <= 1) {
      return null;
    }
    items = portails.map((p) => ({
      key: p.nom,
      label: portailLabel(p.nom),
      value: p.nom,
      disabled: false
    }));
    selectedItem = {
      key: portailActuel.nom,
      label: portailLabel(portailActuel.nom),
      value: portailActuel.nom,
      disabled: false
    };
  } else {
    if (!currentRole || roles.length <= 1) {
      return null;
    }
    items = roles.map((role) => ({
      key: role.roleId,
      label: role.name,
      value: role.name,
      disabled: !isSupportedSwitchRole(role.name)
    }));
    selectedItem = {
      key: currentRole.roleId,
      label: currentRole.name,
      value: currentRole.name,
      disabled: !isSupportedSwitchRole(currentRole.name)
    };
  }

  return (
    <Listbox
      as="div"
      onChange={handleRoleSwitch}
      value={selectedItem}
      className="w-full relative"
    >
      {({ open }) => (
        <>
          <ListboxButton id={id} className={buttonClasses(open)}>
            <div className="flex items-center gap-3 overflow-hidden">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white/20">
                <UserCog className="h-5 w-5" />
              </span>
              <span className="flex min-w-0 flex-col items-start leading-tight">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-white/60">
                  {estModePortail ? 'Portail actuel' : 'Rôle actuel'}
                </span>
                <span className="w-full truncate text-left font-bold">
                  {selectedItem?.label || t('SIDEBAR.SELECT_ROLE')}
                </span>
              </span>
            </div>
            <ChevronDown
              className={clsx(
                'w-4 h-4 transition-transform duration-200 flex-shrink-0',
                open ? 'rotate-180' : ''
              )}
            />
          </ListboxButton>
          <ListboxOptions
            anchor={{ to: 'top', gap: 4, padding: 16 }}
            className={menuClasses}
          >
            {items.map((item) => (
              <ListboxOption
                key={item.key}
                value={item}
                disabled={item.disabled}
                className={({ focus, selected }) =>
                  getItemClasses(focus, selected, item.disabled)
                }
              >
                <span className="flex w-full items-center justify-between gap-2">
                  {item.label}
                  {item.disabled && (
                    <span className="text-[10px] uppercase tracking-wide text-gray-400">
                      Bientôt
                    </span>
                  )}
                </span>
              </ListboxOption>
            ))}
          </ListboxOptions>
        </>
      )}
    </Listbox>
  );
};
